import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical, EyeOff } from 'lucide-react';
import { SortableList } from './SortableList';
import { ItemActions } from './ItemActions';
import { PreviewModal } from './PreviewModal';

const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const { toast } = useToast();

  const fetchProjects = async () => {
    try {
      const res = await adminApi.get('/projects');
      setProjects(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing.id && projects.find((p) => p.id === editing.id)) {
        await adminApi.put(`/projects/${editing.id}`, editing);
      } else {
        await adminApi.post('/projects', { ...editing, id: undefined });
      }
      toast({ title: 'Saved', description: 'Project saved successfully' });
      setEditing(null);
      fetchProjects();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save project', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this project?')) return;
    try {
      await adminApi.delete(`/projects/${id}`);
      toast({ title: 'Deleted', description: 'Project removed' });
      fetchProjects();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleReorder = async (reordered) => {
    setProjects(reordered);
    try {
      await adminApi.put('/projects/reorder', { ids: reordered.map((r) => r.id) });
    } catch (err) {
      toast({ title: 'Error', description: 'Reorder failed', variant: 'destructive' });
    }
  };

  const newProject = () =>
    setEditing({
      icon: '📚',
      badge: '',
      title: '',
      description: '',
      link: '#programmes',
      order: projects.length,
      published: true,
    });

  return (
    <div className="space-y-6" data-testid="projects-manager">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Projects / Programmes</h2>
        <Button onClick={newProject} className="bg-[#1565C0]" data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>{editing.id ? 'Edit Project' : 'New Project'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Icon (Emoji)</Label>
                  <Input
                    value={editing.icon || ''}
                    onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                    placeholder="📚"
                    data-testid="project-icon-input"
                  />
                </div>
                <div>
                  <Label>Badge</Label>
                  <Input
                    value={editing.badge || ''}
                    onChange={(e) => setEditing({ ...editing, badge: e.target.value })}
                    placeholder="Core Activity"
                    data-testid="project-badge-input"
                  />
                </div>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={editing.title || ''}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  required
                  data-testid="project-title-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={4}
                  required
                  data-testid="project-description-input"
                />
              </div>
              <div>
                <Label>Link</Label>
                <Input
                  value={editing.link || ''}
                  onChange={(e) => setEditing({ ...editing, link: e.target.value })}
                  placeholder="#programmes"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editing.published !== false}
                  onCheckedChange={(v) => setEditing({ ...editing, published: v })}
                />
                <Label>Published</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#1565C0]" data-testid="save-project-btn">
                  {loading ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={projects}
        onReorder={handleReorder}
        renderItem={(project) => (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{project.icon}</span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{project.badge}</span>
                    {project.published === false && (
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Draft
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold">{project.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                </div>
                <ItemActions
                  collection="projects"
                  item={project}
                  onChanged={fetchProjects}
                  onEdit={setEditing}
                  onDelete={handleDelete}
                  onPreview={setPreviewing}
                />
              </div>
            </CardContent>
          </Card>
        )}
      />
      <PreviewModal kind="project" item={previewing} onClose={() => setPreviewing(null)} />
      {projects.length === 0 && !editing && (
        <p className="text-center text-gray-500 py-8">No projects yet. Click "Add Project" to create one.</p>
      )}
    </div>
  );
};

export default ProjectsManager;
